/**
 * @author    Nikko Renolds
 * @package   Trap Tycoon
 * @link      https://github.com/Ni1kko/Trap-Tycoon
 * @copyright (c) Fallox Developments2026.
 * @license   MIT
 * @description Reimagined BANDO CRACK LAB with immersive recipe cards and pricing info.
 */

import React, { useState } from "react";
import {
  Lock,
  Search,
  FlaskConical,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Flame,
} from "lucide-react";

export default function Cookbook({ userStats, recipes, onUnlock, onCook }) {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("skill");

  // ADD THIS DEFENSIVE CHECK
  if (!userStats) {
    return (
      <div className="p-10 text-center text-zinc-500 font-mono uppercase italic">
        Loading lab data...
      </div>
    );
  }

  // Determine rarity based on Skill Level
  const getRarityTheme = (skill) => {
    if (skill >= 10)
      return {
        color: "text-yellow-400",
        border: "border-yellow-600",
        bg: "bg-yellow-900/10",
        label: "LEGENDARY",
        shadow: "shadow-[0_0_15px_rgba(234,179,8,0.2)]",
      };
    if (skill >= 7)
      return {
        color: "text-purple-400",
        border: "border-purple-600",
        bg: "bg-purple-900/10",
        label: "EPIC",
        shadow: "shadow-[0_0_10px_rgba(168,85,247,0.1)]",
      };
    if (skill >= 4)
      return {
        color: "text-blue-400",
        border: "border-blue-600",
        bg: "bg-blue-900/10",
        label: "RARE",
        shadow: "shadow-[0_0_10px_rgba(59,130,246,0.1)]",
      };
    return {
      color: "text-zinc-400",
      border: "border-zinc-700",
      bg: "bg-zinc-900/30",
      label: "COMMON",
      shadow: "",
    };
  };

  const filteredRecipes = recipes
    .filter((r) => r.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === "skill") return a.min_skill - b.min_skill;
      if (sortBy === "cost") return a.unlock_cost - b.unlock_cost;
      if (sortBy === "price") return b.value_per_unit - a.value_per_unit;
      return 0;
    });

  return (
    <div className="bg-zinc-950 border border-zinc-800 p-6 font-mono animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-end mb-8 border-b border-zinc-800 pb-4">
        <div>
          <h2 className="text-4xl font-black italic text-white flex items-center gap-3 uppercase tracking-tighter">
            <FlaskConical className="text-green-500 animate-pulse" size={32} />
            Bando Crack Lab
          </h2>
          <p className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">
            Manufacturing & Research Division
          </p>
        </div>
        <div className="text-right mt-4 md:mt-0">
          <p className="text-[10px] text-zinc-500 uppercase font-bold">
            Your Cooking Skill
          </p>
          <p className="text-2xl font-black text-blue-500">
            LEVEL {userStats.cooking_skill}
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-4 mb-8 bg-zinc-900/30 p-4 border border-zinc-800 backdrop-blur-sm sticky top-0 z-10 shadow-xl">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 text-zinc-600" size={14} />
          <input
            className="w-full bg-black border border-zinc-700 p-2 pl-9 text-xs text-white outline-none focus:border-green-600 transition-colors uppercase placeholder:text-zinc-700"
            placeholder="SEARCH FORMULAS..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="bg-black border border-zinc-700 p-2 text-[10px] text-white uppercase outline-none focus:border-blue-600 cursor-pointer"
        >
          <option value="skill">Sort: Complexity (Low-High)</option>
          <option value="cost">Sort: Unlock Cost</option>
          <option value="price">Sort: Street Price (High-Low)</option>
        </select>
      </div>

      {/* Recipe Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredRecipes.map((recipe) => {
          const theme = getRarityTheme(recipe.min_skill);
          const isUnlockable =
            !recipe.unlocked &&
            userStats.cash >= recipe.unlock_cost &&
            userStats.cooking_skill >= recipe.min_skill;
          const isCookable =
            recipe.unlocked && userStats.cooking_skill >= recipe.min_skill;

          return (
            <div
              key={recipe.id}
              className={`relative flex flex-col justify-between border-2 bg-black transition-all hover:scale-[1.02] hover:z-10 group ${theme.border} ${theme.shadow}`}
            >
              {/* Card Header */}
              <div
                className={`p-3 border-b ${theme.border} ${theme.bg} flex justify-between items-start`}
              >
                <div>
                  <span
                    className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 border ${theme.border} ${theme.color} bg-black`}
                  >
                    {theme.label}
                  </span>
                  <h3 className="text-lg font-black text-white mt-2 leading-none uppercase italic">
                    {recipe.name}
                  </h3>
                </div>
                {recipe.unlocked ? (
                  <FlaskConical size={18} className="text-green-500" />
                ) : (
                  <Lock size={18} className="text-zinc-600" />
                )}
              </div>

              {/* Stats Body */}
              <div className="p-4 space-y-3 flex-1">
                {/* Price Display */}
                <div className="flex justify-between items-center bg-zinc-900/50 p-2 border border-zinc-800">
                  <span className="text-[10px] text-zinc-500 font-bold uppercase flex items-center gap-1">
                    <DollarSign size={12} className="text-green-600" /> Street
                    Price
                  </span>
                  <span className="text-sm font-black text-green-400">
                    ${recipe.value_per_unit}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-[10px] uppercase">
                  <div>
                    <span className="block text-zinc-600 font-bold">
                      Complexity
                    </span>
                    <span
                      className={`font-black ${
                        userStats.cooking_skill >= recipe.min_skill
                          ? "text-white"
                          : "text-red-500"
                      }`}
                    >
                      LVL {recipe.min_skill}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="block text-zinc-600 font-bold">
                      Batch Yield
                    </span>
                    <span className="font-black text-blue-400">
                      {recipe.yield_min}-{recipe.yield_max}
                    </span>
                  </div>
                </div>

                {/* Progress Bar for Skill */}
                <div className="w-full h-1.5 bg-zinc-900 border border-zinc-800 mt-2 relative">
                  <div
                    className={`h-full transition-all duration-500 ${
                      userStats.cooking_skill >= recipe.min_skill
                        ? "bg-green-600"
                        : "bg-red-600"
                    }`}
                    style={{
                      width: `${Math.min(
                        100,
                        (userStats.cooking_skill / recipe.min_skill) * 100,
                      )}%`,
                    }}
                  />
                </div>
              </div>

              {/* Action Footer */}
              <div className="p-3 border-t border-zinc-900 bg-zinc-950">
                {recipe.unlocked ? (
                  <button
                    onClick={() => onCook(recipe.id)}
                    disabled={!isCookable}
                    className="w-full py-3 bg-green-700 hover:bg-green-600 disabled:bg-zinc-800 disabled:text-zinc-600 text-white font-black uppercase text-xs italic tracking-widest transition-all flex items-center justify-center gap-2 shadow-[0_0_15px_rgba(21,128,61,0.4)] hover:shadow-[0_0_25px_rgba(21,128,61,0.6)]"
                  >
                    <Flame size={14} /> Cook Product
                  </button>
                ) : (
                  <button
                    onClick={() => onUnlock(recipe.id)}
                    disabled={!isUnlockable}
                    className={`w-full py-3 font-black uppercase text-xs italic tracking-widest transition-all border flex items-center justify-center gap-2 ${
                      isUnlockable
                        ? "bg-blue-600 hover:bg-blue-500 text-white border-blue-500 shadow-[0_0_15px_rgba(37,99,235,0.4)]"
                        : "bg-zinc-900 text-zinc-500 border-zinc-800 cursor-not-allowed"
                    }`}
                  >
                    {isUnlockable ? (
                      <>
                        <TrendingUp size={14} /> Learn Formula ($
                        {recipe.unlock_cost.toLocaleString()})
                      </>
                    ) : (
                      <>
                        <AlertTriangle size={14} /> Locked ($
                        {recipe.unlock_cost.toLocaleString()})
                      </>
                    )}
                  </button>
                )}
                {!isCookable && recipe.unlocked && (
                  <p className="text-[9px] text-red-500 text-center mt-2 font-bold uppercase animate-pulse">
                    Skill Level Too Low
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
